// src/cpq_agent/webchat_ui/pipelines.js
// Central Pipelines board: renders each client family as a dev->test->prod
// pipeline strip (env-badged stage nodes joined by the Conduit connector),
// read from GET /api/promotion/pipelines. Read-only in 2b-i; the click->diff
// + run drawer arrive in 2b-ii.
import { api } from "./api.js";
import { getPanelContainer } from "./panel-registry.js";
import { esc } from "./state.js";
import { mountRunDrawer, startPromotion, isRunActive, retryFailed, askAgentForAsset, fixAssetWithAgent, fixFailuresWithAgent, downloadRunLog, beginReverse, cancelReverse, reverseRun, beginDeleteStep, cancelDeleteStep, confirmDeleteStep } from "./promotion-drawer.js";

let _el = null;
let _inited = false;
let _includeOptin = new Set();   // opt-in asset types the operator ticked
let _optinTypes = [];            // available opt-in types (from the pipelines response)
let _previewRoute = null;        // { from, to, toEnv } of the open preview (for re-fetch on toggle)

const _ACTION_ICON  = { create: "plus", update: "edit", delete: "x", in_sync: "check" };
const _ACTION_LABEL = { create: "create", update: "update", delete: "delete", in_sync: "in sync" };

export function initPipelinesPanel() {
  if (_inited) return;
  _el = getPanelContainer("pipelines");
  if (_el) {
    _el.addEventListener("click", _onPanelClick);
    // Enable/disable the reverse confirm button as the user types
    _el.addEventListener("input", ev => {
      if (ev.target.classList.contains("prd-rev-input")) {
        const btn = ev.target.closest(".prd-foot")?.querySelector(".prd-rev-confirm");
        if (btn) btn.disabled = ev.target.value.trim() !== "reverse";
      }
    });
  }
  _inited = true;
}

function _onPanelClick(ev) {
  if (ev.target.closest(".prd-retry")) { retryFailed(); return; }
  if (ev.target.closest(".prd-fix")) { fixFailuresWithAgent(); return; }
  if (ev.target.closest(".prd-reverse")) { beginReverse(); return; }
  if (ev.target.closest(".prd-rev-cancel")) { cancelReverse(); return; }
  if (ev.target.closest(".prd-rev-confirm")) { reverseRun(); return; }

  // Per-item delete controls in the reverse result
  const delItem = ev.target.closest(".prd-delitem");
  if (delItem) {
    const eid = delItem.getAttribute("data-entry-id");
    if (eid) { beginDeleteStep(eid); }
    return;
  }
  const delOk = ev.target.closest(".prd-delitem-ok");
  if (delOk) {
    const eid = delOk.getAttribute("data-entry-id");
    const fam = delOk.getAttribute("data-family");
    const aid = delOk.getAttribute("data-asset-id");
    if (eid && fam && aid != null) { confirmDeleteStep(eid, fam, aid); }
    return;
  }
  const delCancel = ev.target.closest(".prd-delitem-cancel");
  if (delCancel) {
    const eid = delCancel.getAttribute("data-entry-id");
    if (eid) { cancelDeleteStep(eid); }
    return;
  }

  // Fix-manually (edit icon): prefill the composer without auto-sending.
  const fixManual = ev.target.closest(".prd-fixmanual");
  if (fixManual) { askAgentForAsset(fixManual.getAttribute("data-asset-name")); return; }

  // Ask-agent (sparkle per-row): auto-send THIS single asset to the agent.
  const ask = ev.target.closest(".prd-ask");
  if (ask) { fixAssetWithAgent(ask.getAttribute("data-asset-name")); return; }

  // Download log: client-side JSON export of current run.
  if (ev.target.closest(".prd-dl")) { downloadRunLog(); return; }

  // Opt-in toggle — must come before .pl-back/.pl-conduit so checkbox clicks don't fall through.
  const optin = ev.target.closest(".pl-optin");
  if (optin) {
    const t = optin.getAttribute("data-optin-type");
    if (_includeOptin.has(t)) _includeOptin.delete(t); else _includeOptin.add(t);
    if (_previewRoute) _openPreview(_previewRoute.from, _previewRoute.to, _previewRoute.toEnv);
    return;
  }

  const promote = ev.target.closest(".pl-promote");
  if (promote) {
    const from = promote.getAttribute("data-from");
    const to   = promote.getAttribute("data-to");
    const confirmEl = promote.closest(".pl-prev-foot")?.querySelector(".pl-confirm");
    const confirm = confirmEl ? confirmEl.value.trim() : "";
    const container = _el || getPanelContainer("pipelines");
    const shell = container?.querySelector(".pl-board-shell") || container;
    if (shell) { mountRunDrawer(shell); }
    startPromotion({ source: from, target: to, confirm, includeOptin: [..._includeOptin] });
    return;
  }

  const conduit = ev.target.closest(".pl-conduit");
  if (conduit) {
    const from  = conduit.getAttribute("data-from");
    const to    = conduit.getAttribute("data-to");
    const toEnv = conduit.getAttribute("data-to-env");
    if (from && to) _openPreview(from, to, toEnv || "");
    return;
  }
  if (ev.target.closest(".pl-back")) refreshPipelines();
}

async function _openPreview(from, to, toEnv) {
  _previewRoute = { from, to, toEnv };
  const container = _el || getPanelContainer("pipelines");
  const shell = container?.querySelector(".pl-board-shell") || container;
  if (!shell) return;
  shell.innerHTML = `<div class="pl-loading">Computing diff ${esc(from)} → ${esc(to)}…</div>`;
  let plan;
  try {
    plan = await api.promotionPlan(from, to, [..._includeOptin]);
  } catch (e) {
    shell.innerHTML = `<div class="pl-error">Could not compute diff: ${esc(String(e && e.message || e))}</div>`;
    return;
  }
  shell.innerHTML = _renderPreview(from, to, toEnv || "", plan || {});
}

function _renderPreview(from, to, toEnv, plan) {
  const back = `<button class="pl-back" title="Back to pipelines">← Pipelines</button>`;
  const head = `<div class="pl-prev-head">${back}
    <span class="pl-prev-route"><span class="pl-mono">${esc(from)}</span> → <span class="pl-mono">${esc(to)}</span></span></div>`;
  // Opt-in row — shown in all allowed states (changes + in-sync) so operator can
  // include opt-in types even when the base diff is empty.
  const optinRow = (plan.allowed !== false && _optinTypes.length)
    ? `<div class="pl-optin-row">${_optinTypes.map(t =>
        `<label class="pl-optin-label"><input type="checkbox" class="pl-optin" data-optin-type="${esc(t)}"
          ${_includeOptin.has(t) ? "checked" : ""}/> Include ${esc(t)}</label>`).join("")}</div>`
    : "";
  if (plan.allowed === false) {
    return `<div class="pl-preview">${head}<div class="pl-prev-blocked">${esc(plan.reason || "Promotion not allowed.")}</div></div>`;
  }
  const plans = Array.isArray(plan.plans) ? plan.plans : [];
  if (plans.length === 0) {
    return `<div class="pl-preview">${head}${optinRow}<div class="pl-prev-empty">In sync — nothing to promote.</div></div>`;
  }
  // Group by type, preserving first-seen order.
  const groups = new Map();
  for (const p of plans) {
    if (!groups.has(p.type)) groups.set(p.type, []);
    groups.get(p.type).push(p);
  }
  const rows = [...groups.entries()].map(([type, items]) => `
    <div class="pl-prev-group">
      <div class="pl-prev-type">${esc(type)}</div>
      ${items.map(p => {
        const a = String(p.action || "");
        const icon = _ACTION_ICON[a] || "circle";
        const advisory = p.detail
          ? `<span class="ic ic-sm ic-alert-triangle pl-advisory" aria-label="formula advisory" title="${esc(p.detail)}"></span>`
          : "";
        return `<div class="pl-prev-row pl-act-${esc(a)}">
          <span class="ic ic-sm ic-${icon} pl-prev-glyph" aria-hidden="true"></span>
          <span class="pl-mono pl-prev-name">${esc(p.name)}</span>
          <span class="pl-prev-action">${esc(_ACTION_LABEL[a] || a)}</span>${advisory}</div>`;
      }).join("")}
    </div>`).join("");
  const confirmInput = toEnv === "prod"
    ? `<input class="pl-confirm" type="text" placeholder="type '${esc(to)}' to confirm" />`
    : "";
  const footer = `<div class="pl-prev-foot">
    ${confirmInput}
    <button class="pl-promote" data-from="${esc(from)}" data-to="${esc(to)}">Review &amp; Promote →</button></div>`;
  return `<div class="pl-preview">${head}${optinRow}
    <div class="pl-prev-count">${plans.length} change${plans.length === 1 ? "" : "s"}</div>
    ${rows}
    ${footer}</div>`;
}

export async function onPipelinesActivated() {
  await refreshPipelines();
}

export async function refreshPipelines() {
  if (isRunActive()) return;   // M1: don't clobber an in-flight promotion drawer
  const el = _el || getPanelContainer("pipelines");
  if (!el) return;
  const shell = el.querySelector(".pl-board-shell") || el;
  shell.innerHTML = `<div class="pl-loading">Loading pipelines…</div>`;
  let data;
  try {
    data = await api.getPipelines();
  } catch (e) {
    shell.innerHTML = `<div class="pl-error">Could not load pipelines: ${esc(String(e && e.message || e))}</div>`;
    return;
  }
  _includeOptin = new Set();
  _previewRoute = null;
  _optinTypes = Array.isArray(data && data.optin_types) ? data.optin_types : [];
  shell.innerHTML = _renderBoard(data || {});
}

const _ENV_ORDER = ["dev", "test", "prod"];

function _renderBoard(data) {
  const pipelines = Array.isArray(data.pipelines) ? data.pipelines : [];
  const tenants = Array.isArray(data.tenants) ? data.tenants : [];
  const connected = new Map(tenants.map(t => [t.name, t]));

  if (pipelines.length === 0) {
    return `<div class="pl-empty">No promotion pipelines yet. Assign a <b>family</b> and
      <b>environment</b> to at least two tenants (via the tenant dropdown) to form a pipeline.</div>`;
  }

  const strips = pipelines.map(p => {
    const stages = (p.stages || []).slice().sort(
      (a, b) => _ENV_ORDER.indexOf(a.env) - _ENV_ORDER.indexOf(b.env));
    const parts = [];
    stages.forEach((s, i) => {
      parts.push(_renderNode(s, connected.get(s.tenant)));
      if (i < stages.length - 1) parts.push(_renderConduit(s, stages[i + 1]));
    });
    return `
      <div class="pl-strip" data-family="${esc(p.family || "")}">
        <div class="pl-fam"><span class="pl-fam-dot"></span>${esc(p.family || "")}</div>
        <div class="pl-flow">${parts.join("")}</div>
      </div>`;
  }).join("");

  // Ungrouped tenants (no family / no env) — a small footer for visibility.
  const grouped = new Set(pipelines.flatMap(p => (p.stages || []).map(s => s.tenant)));
  const ungrouped = tenants.filter(t => !grouped.has(t.name));
  const ungroupedHtml = ungrouped.length
    ? `<div class="pl-ungrouped"><span class="pl-ungrouped-label">Ungrouped</span>
        ${ungrouped.map(t => `<span class="pl-chip">${esc(t.name)}</span>`).join("")}</div>`
    : "";

  return `<div class="pl-board">${strips}${ungroupedHtml}</div>`;
}

function _renderNode(stage, tenant) {
  const env = String(stage.env || "").toLowerCase();
  const isConn = !!(tenant && tenant.connected);
  const isActive = !!(tenant && tenant.active);
  return `
    <div class="pl-node${isActive ? " pl-node-active" : ""}">
      <div class="pl-node-env pl-env-${esc(env)}">${esc(env)}</div>
      <div class="pl-node-tenant" title="${esc(stage.tenant || "")}">${esc(stage.tenant || "")}</div>
      <div class="pl-node-status">
        <span class="pl-dot ${isConn ? "pl-dot-on" : ""}"></span>${isConn ? "connected" : "idle"}
      </div>
    </div>`;
}

function _renderConduit(from, to) {
  return `
    <div class="pl-conduit" data-from="${esc(from.tenant || "")}" data-to="${esc(to.tenant || "")}"
         data-to-env="${esc(String(to.env || ""))}"
         title="Click to preview ${esc(String(from.env || ""))} → ${esc(String(to.env || ""))} promotion diff">
      <div class="pl-conduit-pipe"></div>
    </div>`;
}
